import type { FC } from 'react';
import React, { useState } from 'react';
import { injectIntl, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button';
import CheckCircleOutlineIcon from '@atlaskit/icon/glyph/check-circle-outline';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { N20, N30, N900, B100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { Box, xcss } from '@atlaskit/primitives';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import { useBooleanFeatureFlag } from '@confluence/session-data';

import { SECONDARY_COLORS } from '../colors';
import { sharedi18n } from '../sharedi18n';
import {
	ContentStateColor,
	ContentStateRestrictionLevel,
	type ColorAppearance,
} from '../ContentStateColor';

import { type ContentStateButtonBaseProps, type ContentStateButtonProps } from './types';
import { i18n } from './i18n';
import { determineHoverColor, determineSecondaryColor } from './helpers';
import { PageTitleContentStateButton } from './PageTitleContentStateButton';

const contentStateNameStyles = xcss({
	marginLeft: 'space.025',
	marginRight: 'space.025',
	marginTop: 'space.0',
	marginBottom: 'space.0',
});

const subtleContentStateNameStyles = xcss({
	fontSize: '12px',
	fontWeight: 'font.weight.regular',
	color: 'color.text.subtle',
});

const chevronDownIconWrapperStyles = xcss({
	marginRight: 'space.negative.050',
});

const getContentStateButtonBackgroundColor = (
	isWarning: boolean,
	isSelected: boolean,
	isHovered: boolean,
	color: string,
	isContentStateVisualExperimentEnabled: boolean,
	hoverColor: string,
) => {
	if (isWarning) {
		return SECONDARY_COLORS.RED;
	}
	if (isSelected) {
		return color;
	}
	if (isContentStateVisualExperimentEnabled) {
		return isHovered ? hoverColor : color;
	} else {
		return isHovered
			? token('color.background.neutral.hovered', N30)
			: token('color.background.neutral', N20);
	}
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HasContentStateButton = styled.button({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	height: '32px',
	cursor: 'pointer',
	paddingLeft: token('space.075', '6px'),
	paddingRight: token('space.075', '6px'),
	color: token('color.text', N900),
	lineHeight: 0,
	fontFamily: 'inherit',
	fontSize: 'inherit',
	fontWeight: 500,
	borderRadius: '3px',
	border: 'none',
	outlineColor: token('color.border.focused', B100),
	'&:disabled': {
		cursor: 'not-allowed',
		opacity: 0.7,
		backgroundColor: token('color.background.disabled', N20),
	},
});

const addContentStateButtonStyles = xcss({
	marginRight: 'space.050',
});

const ContentStateButtonBase: FC<ContentStateButtonBaseProps> = ({
	triggerProps,
	togglePopup,
	contentState,
	isSelected,
	isWarning,
	userOffline,
	contentStateButtonRef,
	floatingToolbarButtonStyle,
	appearance,
	primaryColor,
	secondaryColor,
	hoverColor,
	showChevron,
	colorAppearance,
	isContentStateVisualExperimentEnabled,
}) => {
	const intl = useIntl();
	const [isHovered, setIsHovered] = useState<boolean>(false);
	if (!contentState) {
		return null;
	}
	const isContentStateSelectorDisabled = contentState.isCallerPermitted === false || userOffline;
	const tooltipContent =
		contentState.restrictionLevel === ContentStateRestrictionLevel.PAGE_OWNER
			? sharedi18n.verifiedRestricted
			: i18n.changeStatus;
	const buttonStyles = {
		...floatingToolbarButtonStyle,
		backgroundColor: getContentStateButtonBackgroundColor(
			isWarning,
			isSelected,
			isHovered,
			secondaryColor,
			isContentStateVisualExperimentEnabled,
			hoverColor,
		),
	};
	return (
		//@ts-ignore
		<div ref={contentStateButtonRef}>
			<Tooltip content={intl.formatMessage(tooltipContent)} position="bottom">
				<HasContentStateButton
					{...triggerProps}
					appearance={appearance}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={buttonStyles}
					onClick={togglePopup}
					isSelected={isSelected}
					disabled={isContentStateSelectorDisabled}
					isWarning={isWarning}
					color={secondaryColor}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					data-testid="content-state-trigger"
					type="button"
				>
					<ContentStateColor
						dataTestId="button-color-icon"
						appearance={colorAppearance}
						color={primaryColor}
						restrictionLevel={contentState.restrictionLevel}
						isWarning={isWarning}
					/>
					<Box
						xcss={[contentStateNameStyles, appearance === 'subtle' && subtleContentStateNameStyles]}
					>
						{contentState.name}
					</Box>
					{showChevron && (
						<Box xcss={chevronDownIconWrapperStyles}>
							<ChevronDownIcon size="small" label={intl.formatMessage(i18n.showMoreStatuses)} />
						</Box>
					)}
				</HasContentStateButton>
			</Tooltip>
		</div>
	);
};

const ContentStateButtonComponent: FC<ContentStateButtonProps> = ({
	triggerProps,
	togglePopup,
	contentState,
	isSelected,
	isWarning,
	userOffline,
	contentStateButtonRef,
	intl: { formatMessage },
	floatingToolbarButtonStyle,
	isCustomSitesPageTitleFFOn,
	isPageTitleConfigPanelOpen,
}) => {
	const isContentStateVisualExperimentEnabled = FeatureGates.getExperimentValue(
		'content_state_visual_appearance_experiment',
		'isEnabled',
		false,
	);

	const isLivePage = useIsLivePage();
	const isNewLivePageBylineFeatureFlagEnabled = useBooleanFeatureFlag(
		'confluence.frontend.custom-sites.new-live-pages-byline',
	);
	const isNewLivePageByline =
		isLivePage && isCustomSitesPageTitleFFOn && isNewLivePageBylineFeatureFlagEnabled;

	const getButtonProps = (editorContentState: any) => {
		const primaryColor = editorContentState.color;
		if (isNewLivePageByline) {
			return {
				appearance: 'subtle',
				primaryColor,
				secondaryColor: process.env.REACT_SSR
					? '#00000000'
					: token('utility.elevation.surface.current', '#FFFFFF'),
				hoverColor: token('elevation.surface.hovered', '#FAFBFC'),
				showChevron: false,
				colorAppearance: 'subtleContentStateButton' as ColorAppearance,
			};
		}

		const secondaryColor = determineSecondaryColor(primaryColor);
		const hoverColor = determineHoverColor(secondaryColor);
		return {
			appearance: 'default',
			primaryColor,
			secondaryColor,
			hoverColor,
			showChevron: true,
			colorAppearance: 'contentStateButton' as ColorAppearance,
		};
	};

	if (isPageTitleConfigPanelOpen && isLivePage) {
		return (
			<PageTitleContentStateButton
				triggerProps={triggerProps}
				togglePopup={togglePopup}
				contentState={contentState}
				isSelected={isSelected}
				isWarning={isWarning}
				userOffline={userOffline}
				contentStateButtonRef={contentStateButtonRef}
			/>
		);
	}

	if (contentState) {
		const buttonProps = getButtonProps(contentState);
		return (
			<ContentStateButtonBase
				triggerProps={triggerProps}
				togglePopup={togglePopup}
				contentState={contentState}
				isSelected={isSelected}
				isWarning={isWarning}
				userOffline={userOffline}
				contentStateButtonRef={contentStateButtonRef}
				floatingToolbarButtonStyle={floatingToolbarButtonStyle}
				isContentStateVisualExperimentEnabled={isContentStateVisualExperimentEnabled}
				{...buttonProps}
			/>
		);
	}

	// No content state case
	return (
		// @ts-ignore
		<div ref={contentStateButtonRef}>
			<Box xcss={!isCustomSitesPageTitleFFOn && addContentStateButtonStyles}>
				<Button
					{...triggerProps}
					appearance={isCustomSitesPageTitleFFOn ? 'subtle' : undefined}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={floatingToolbarButtonStyle || {}}
					onClick={togglePopup}
					iconBefore={<CheckCircleOutlineIcon size="small" label="" />}
					isSelected={isSelected}
					spacing="compact"
					testId="content-state-trigger"
					isDisabled={userOffline}
				>
					{isCustomSitesPageTitleFFOn
						? formatMessage(i18n.statusLabel)
						: formatMessage(i18n.addStatus)}
				</Button>
			</Box>
		</div>
	);
};

export const ContentStateButton = injectIntl(ContentStateButtonComponent);
